<template>
  <div
    :class="['search-bar-container', { light }]"
    :style="`width: ${alwaysOpen ? width : 'auto'}`"
  >
    <transition name="slide">
      <v-text-field
        v-show="open"
        ref="text-field"
        :model-value="localSearchTerm"
        single-line
        variant="outlined"
        density="compact"
        hide-details
        :placeholder="placeholder"
        class="search-bar"
        :class="{ open: alwaysOpen, active }"
        rounded
        :menu-props="{ contentClass: 'navbar-search-text-field-items' }"
        @update:model-value="updateSearch"
        @keyup.enter="handleSearch"
        @update:focused="handleTextFieldBlurOrFocus"
      >
        <template #prepend-inner>
          <DpplRoundButton
            v-if="open"
            :light="light"
            class="active-button"
            aria-label="Search"
            @click="handleSearch"
          >
            <SearchIcon class="colored" />
          </DpplRoundButton>
        </template>
      </v-text-field>
    </transition>
    <DpplRoundButton
      v-if="!open"
      :light="light"
      class="search-button"
      @click.capture="handleSearchButtonClick"
    >
      <SearchIcon :class="showTextField || active ? 'colored' : ''" />
    </DpplRoundButton>
  </div>
</template>
<script>
import { nextTick } from 'vue';
import DpplRoundButton from '@/components/datapeople/DpplRoundButton.vue';
import { useTracking } from '@/stores/tracking/tracking';
import SearchIcon from '@/assets/icon-search.svg?component';

export default {
  name: 'DashboardSearchBar',
  components: {
    SearchIcon,
    DpplRoundButton,
  },
  props: {
    light: {
      type: Boolean,
      default: false,
    },
    searchTerm: {
      type: String,
      default: '',
    },
    alwaysOpen: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '440px',
    },
    placeholder: {
      type: String,
      default: 'Write here',
    },
  },
  emits: ['update:search', 'update:modelValue'],
  data() {
    return {
      localSearchTerm: null,
      showTextField: false,
      active: false,
    };
  },
  computed: {
    open() {
      return this.showTextField || this.alwaysOpen;
    },
  },
  watch: {
    searchTerm(newValue) {
      this.localSearchTerm = newValue;
    },
  },
  methods: {
    updateSearch(value) {
      this.localSearchTerm = value;
      this.$emit('update:modelValue', value);
    },
    handleSearch() {
      useTracking().search();
      if (this.localSearchTerm) {
        this.$emit('update:search', this.localSearchTerm);
      } else {
        this.showTextField = false;
      }
    },
    handleTextFieldBlurOrFocus(isFocused) {
      if (isFocused) {
        this.handleTextFieldFocus();
      } else {
        this.handleTextFieldBlur();
      }
    },
    handleTextFieldBlur() {
      this.showTextField = false;
      if (this.alwaysOpen) {
        this.active = false;
      }

      nextTick(() => {
        this.$refs['text-field']?.blur();
      });
    },
    handleTextFieldFocus() {
      if (this.alwaysOpen) {
        this.active = true;
      }
    },
    handleSearchButtonClick() {
      this.showTextField = true;
      nextTick(() => {
        this.$refs['text-field'].focus();
      });
    },
  },
};
</script>
<style scoped lang="less">
.search-bar-container {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
}
.search-bar {
  border: 0;
  display: inline-flex;
  position: absolute;
  width: v-bind(width);
  transition: border 200ms ease;
  background-color: fade(@white, 10%);
  border-radius: 20px;

  .active-button {
    background-color: transparent;
    border-color: transparent;
    width: 20px;
    height: 20px;

    path {
      fill: @icon-white;
      transition: fill 200ms ease;
    }
  }

  .active-button:hover, .active-button:focus {
    border: 0;
  }

  :deep(.v-input__control){
    width: 100%;
  }

  &.v-input {
    :deep(.v-field__outline) {
      color: fade(@white, 40%);
    }

    :deep(.v-field__field) {
      input {
        color: @text-white;
        caret-color: @white;
      }

      input::placeholder {
        color: fade(@text-white, 80%);
      }
    }
  }

  &.open {
    width: inherit;
  }

  &.active {
    &.v-input :deep(.v-field__outline) {
      --v-field-border-width: 1px;
      color: @text-white;
    }
  }

  /* this targets the button inside the expanded field
   which gets placed inside Vuetify's .v-field__append-inner */
  .dppl-round-button.light.v-btn:hover :deep(> .v-btn__overlay) {
    opacity: 0;
  }
}
.light {
  /* this targets the button when field is collapsed
   and the button is a sibling of the field */
  .search-button {
    &.v-btn:hover :deep(> .v-btn__overlay) {
      opacity: 0;
    }
  }
}
:deep(.colored) {
  fill: fade(@white, 20%);
}
:deep(path) {
  fill: @icon-white;
}
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-enter-active, .slide-leave-active {
  transition: all .2s ease;
}
.slide-enter-from, .slide-leave-to {
  width: 56px;
}
.slide-enter-active.v-input :deep(.v-field__field) > input,
.slide-leave-active.v-input :deep(.v-field__field) > input,
.slide-leave-active.v-input :deep(.v-input__append-inner) {
  opacity: 0;
}

.slide-enter-active.v-input :deep(.v-field__outline) {
  --v-field-border-width: 1px;
}
.slide-leave-active + .search-button {
  border: 0;
}
:deep(.v-input__append-inner),
:deep(.v-input__append-inner) {
  position: absolute;
  right: 3px;
  top: 1px;
  height: 16px;
  width: 16px;
}

.slide-enter-from.v-input :deep(.v-field__outline),
.slide-leave-to.v-input :deep(.v-field__outline) {
  --v-field-border-opacity: 1;
  --v-field-border-width: 1px;
}
:deep(.v-field__field) > input {
  font-size: 12px;
  font-weight: 300;
}

</style>

<style>
.navbar-search-text-field-items {
  text-align: left;
}
</style>
